import { FieldValue, LookupFieldValue } from '../models/etrieveContent';
import { DocumentType } from '../services/content-api/content-api.service';

const SUPPORTED_DOCUMENT_CONTENT_PREFIXES = ['J', 'i', '/', 'S'];

/**
 * Returns true when the base64 content begins with a prefix the document viewer can render
 * (PDF: 'J', PNG: 'i', JPEG: '/', TIFF: 'S').
 * @param base64Content
 */
export const isSupportedDocumentContent = (base64Content: string | undefined | null): boolean => {
  if (!base64Content) return false;
  return SUPPORTED_DOCUMENT_CONTENT_PREFIXES.includes(base64Content.charAt(0));
};

/**
 * Converts a Base64 encoded string to a File object
 * @param {string} base64String - The Base64 encoded string representing the file's content
 * @param {string} fileName - The desired name for the resulting File object
 * @param {string} contentType - The MIME type of the file
 * @returns {File} - The resulting File object
 */
export const base64ToFile = (base64String: string, fileName: string, contentType: string): File => {
  // Decode the Base64 string into binary data
  const binaryData = atob(base64String);

  // Create an array buffer from the binary string
  const byteArray = new Uint8Array(binaryData.length);
  for (let i = 0; i < binaryData.length; i++) {
    byteArray[i] = binaryData.charCodeAt(i);
  }

  // Create a Blob from the array buffer
  const blob = new Blob([byteArray], { type: contentType });

  // Create a File object from the Blob with the specified file name
  const file = new File([blob], fileName, { type: contentType });

  return file;
};

/**
 * Type guard function to determine if a value is of type LookupFieldValue
 * @param {any} value - The value to check
 * @returns {boolean} - True if the value is a LookupFieldValue, false otherwise
 */
const isLookupField = (value: any): value is LookupFieldValue => {
  return value && typeof value === 'object' && 'lookupTypeCode' in value;
};

/**
 * Builds a URL to Etrieve Content using the ecmDocId.
 * @param ecmDocId The ECM document ID to include in the URL for direct navigation to the document in the content viewer. If omitted or invalid, the base content URL is returned.
 * @returns The content viewer URL, or an empty string if the base content URL is not configured in localStorage.
 */
export const buildContentUrl = (ecmDocId: number | null | undefined): string => {
  const baseContentUrl = localStorage.getItem('contentURL') || '';
  if (!baseContentUrl) return '';

  return ecmDocId != null
    ? `${baseContentUrl}/#advancedSearch=(-6!1!${encodeURIComponent(ecmDocId.toString())})`
    : baseContentUrl;
};
